import { createContext, useContext, useEffect, useRef, useCallback, useMemo } from 'react';
import { toast } from 'sonner';
import { useAuth } from './AuthContext';
import { getDueReminders, dismissReminder } from '../lib/api';
import { Bell, Calendar } from 'lucide-react';

const CalendarReminderContext = createContext({});

export const useCalendarReminders = () => useContext(CalendarReminderContext);

// Custom toast for reminders with dismiss action
const ReminderToast = ({ event, onDismiss }) => (
    <div className="flex items-start gap-3">
        <div 
            className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
            style={{ backgroundColor: event.color || '#16a34a' }}
        >
            <Bell className="w-5 h-5 text-white" />
        </div>
        <div className="flex-1 min-w-0">
            <p className="font-semibold text-sm">{event.title}</p>
            <p className="text-xs text-muted-foreground flex items-center gap-1 mt-0.5">
                <Calendar className="w-3 h-3" />
                {new Date(event.date + 'T' + (event.start_time || '00:00')).toLocaleString('de-DE', {
                    weekday: 'short',
                    day: '2-digit',
                    month: '2-digit',
                    hour: '2-digit',
                    minute: '2-digit'
                })}
            </p>
            {event.description && (
                <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{event.description}</p>
            )}
        </div>
    </div>
);

export const CalendarReminderProvider = ({ children }) => {
    const { user } = useAuth();
    const shownRemindersRef = useRef(new Set());
    const intervalRef = useRef(null);

    const checkReminders = useCallback(async () => {
        // Only check for admins
        if (!user || user.role !== 'ADMIN') return;

        try {
            const response = await getDueReminders();
            const reminders = response.data || [];

            for (const reminder of reminders) {
                // Skip if already shown in this session
                if (shownRemindersRef.current.has(reminder.id)) continue;

                // Mark as shown
                shownRemindersRef.current.add(reminder.id);

                // Show toast notification
                toast(
                    <ReminderToast event={reminder} />,
                    {
                        duration: 30000, // 30 seconds
                        action: {
                            label: 'OK',
                            onClick: async () => {
                                try {
                                    await dismissReminder(reminder.id);
                                } catch {
                                    // Silent dismiss error
                                }
                            }
                        },
                        onDismiss: async () => {
                            try {
                                await dismissReminder(reminder.id);
                            } catch {
                                // Silent dismiss error
                            }
                        }
                    }
                );

                // Play notification sound if available
                try {
                    const audio = new Audio('/notification.mp3');
                    audio.volume = 0.3;
                    audio.play().catch(() => {}); // Ignore if autoplay blocked
                } catch {
                    // Silent audio error
                }
            }
        } catch (error) {
            // Silently fail - don't spam console
            if (error.response?.status !== 401) {
                // Reminder check failed - silent
            }
        }
    }, [user]);

    useEffect(() => {
        // Only start polling for admin users
        if (!user || user.role !== 'ADMIN') {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
                intervalRef.current = null;
            }
            return;
        }

        // Initial check
        checkReminders();

        // Poll every 60 seconds
        intervalRef.current = setInterval(checkReminders, 60000);

        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
            }
        };
    }, [user, checkReminders]);

    const contextValue = useMemo(() => ({ checkReminders }), [checkReminders]);

    return (
        <CalendarReminderContext.Provider value={contextValue}>
            {children}
        </CalendarReminderContext.Provider>
    );
};
